import { useEffect, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TOP_UPS, type AlertView, type CallFilter, type CallView, type CreditLot, type ExportView, type LedgerEntry, type Page } from "@joogoos/contracts";
import { useCloseWorkspace, useCreateWorkspace, useSwitchWorkspace, useWorkspace, useWorkspaces } from "../ui/workspace.js";
import { RenameWorkspace } from "../ui/RenameWorkspace.js";
import { api, ApiError, page } from "../api.js";
import { RaiseLimit, TopUpPacks, useMoneyMoved } from "../ui/money.js";
import { authClient } from "../auth.js";
import { Icon } from "../ui/icons.js";
import { Pager } from "../ui/Pager.js";
import { Help } from "./Help.js";
import { Reports } from "./Reports.js";
import { REPORT_NEWS_KEY, useReportNews } from "../reports/News.js";
import { WhatsNew } from "./WhatsNew.js";

export type Panel = "you" | "workspace" | "credits" | "spending" | "settings" | "reports" | "help" | "new";

const PANELS: Array<[Panel, string, string]> = [
  ["you", "You", "person"],
  ["workspace", "Workspace", "shop"],
  ["credits", "Credits", "coin"],
  ["spending", "Spending", "analytics"],
  ["settings", "Settings", "wand"],
  ["reports", "Your reports", "list"],
  ["help", "Help", "chat"],
  ["new", "What's new", "refresh"],
];

/**
 * Settings and billing, in one dialog (docs/06; the old client had the same shape and it was right — 08).
 *
 * Four panels about the account, and three about the product (ADR 0024, ADR 0026): **Your reports**, the workspace's
 * reports and their threads; **Help**, one page per screen written about the product as it is today (docs/19) — listed
 * here, and opened by name when the Help dialog answers from one; and
 * **What's new**, each release as a customer reads it. **You** is who is signed in. **Workspace** is which business this is and the way out of
 * it. **Credits** is the whole of billing, because there is no subscription anywhere in this product: money is bought
 * in lots, drains oldest first and expires a year later (ADR 0002, 0012) — and, under that, every provider call it has
 * been spent on. **Settings** is what is left: channels and what we email about.
 *
 * The call list sits inside Credits rather than in a panel of its own because it is the same subject read closer:
 * money in, money out, and then exactly what the out was. A product that spends a customer's money one call at a
 * time owes them that list rather than a total, and it is already recorded for the guards' sake (13), so showing
 * it costs nothing and refusing to would be a choice.
 */
export function Account({ open, panel, email, name, onPanel, onClose, report, onReport, help, onHelp, onAsk, closing, onClosing, locked = false }: {
  open: boolean;
  panel: Panel;
  email: string;
  name: string | null;
  onPanel: (p: Panel) => void;
  onClose: () => void;
  /** The report Your reports has open. Held by the shell when it passes `onReport`, so a report link can open one. */
  report?: number | null | undefined;
  onReport?: ((id: number | null) => void) | undefined;
  /** The help page Help has open, by slug. Held by the shell when it passes `onHelp`, so the Report dialog can open one. */
  help?: string | null | undefined;
  onHelp?: ((slug: string | null) => void) | undefined;
  /** Opens Help's own box — asking, and the report form in it — over this dialog. The shell owns it (App.tsx). */
  onAsk?: (() => void) | undefined;
  /** The workspace whose close is being asked. Held by the shell when it passes `onClosing`, so Delete in the account menu opens it here. */
  closing?: string | null | undefined;
  onClosing?: ((id: string | null) => void) | undefined;
  /**
   * Held on Credits with no way out but buying or signing out: an account that has never bought credit (ADR 0048).
   * Nothing behind it can do anything for them yet, so the dialog is the front door rather than a page of its own.
   */
  locked?: boolean;
}) {
  const [ownReport, setOwnReport] = useState<number | null>(null);
  const selected = onReport ? (report ?? null) : ownReport;
  const select = onReport ?? setOwnReport;
  const [ownHelp, setOwnHelp] = useState<string | null>(null);
  const helpOpen = onHelp ? (help ?? null) : ownHelp;
  const pickHelp = onHelp ?? setOwnHelp;
  const [ownClosing, setOwnClosing] = useState<string | null>(null);
  const closingOpen = onClosing ? (closing ?? null) : ownClosing;
  const pickClosing = onClosing ?? setOwnClosing;
  useEffect(() => {
    if (!open || locked) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [open, locked, onClose]);
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center md:p-4" role="dialog" aria-modal="true" aria-label="Settings and billing">
      <div className="absolute inset-0 bg-ink/35" onClick={locked ? undefined : onClose} />
      {/* On a phone the dialog is the screen, and its panels are a row to scroll across rather than a column beside it (03 §layout). */}
      <div className="relative w-full max-w-[900px] h-full md:h-[min(620px,88vh)] bg-page md:rounded-2xl shadow-[0_24px_70px_rgba(23,23,26,.35)] flex flex-col md:flex-row overflow-hidden">
        <nav className="shrink-0 md:w-52 bg-rail border-b md:border-b-0 md:border-r border-line p-2 md:p-3 flex md:flex-col gap-1 overflow-x-auto">
          <div className="hidden md:block px-2.5 pt-1 pb-3">
            <div className="text-[13px] font-semibold truncate">{name ?? email}</div>
            <div className="text-[11.5px] text-muted truncate">{email}</div>
          </div>
          {PANELS.map(([p, label, icon]) => (
            <button
              key={p}
              disabled={locked && p !== "credits"}
              onClick={() => { if (p === "reports") select(null); if (p === "help") pickHelp(null); onPanel(p); }}
              className={`shrink-0 whitespace-nowrap flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] text-left ${
                panel === p ? "bg-panel border border-line font-semibold shadow-[0_1px_2px_rgba(23,23,26,.06)]" : "text-ink-2 hover:bg-panel/60"
              } disabled:opacity-40 disabled:hover:bg-transparent`}
            >
              <Icon name={icon} size={15} className="text-muted" /> {label}
            </button>
          ))}
          <button
            onClick={() => authClient.signOut().then(() => location.reload())}
            className="md:mt-auto shrink-0 whitespace-nowrap flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] text-ink-2 hover:bg-panel/60"
          >
            <Icon name="out" size={15} className="text-muted" /> Sign out
          </button>
        </nav>

        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-line">
            <h2 className="text-[15px] font-semibold">{PANELS.find(([p]) => p === panel)?.[1]}</h2>
            {locked ? null : (
              <button onClick={onClose} title="Close" aria-label="Close" className="ml-auto text-faint hover:text-ink">
                <Icon name="x" size={17} />
              </button>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
            {panel === "you" ? <You email={email} name={name} /> : null}
            {panel === "workspace" ? <Workspace closing={closingOpen} onClosing={pickClosing} /> : null}
            {panel === "credits" ? <Credits /> : null}
            {panel === "spending" ? <Spending /> : null}
            {panel === "settings" ? <Settings /> : null}
            {panel === "reports" ? <Reports selected={selected} onSelect={select} /> : null}
            {panel === "help" ? <Help selected={helpOpen} onSelect={pickHelp} onAsk={onAsk} /> : null}
            {panel === "new" ? <WhatsNew /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function You({ email, name }: { email: string; name: string | null }) {
  return (
    <div className="flex flex-col gap-4 max-w-[520px]">
      <Field label="Name" value={name ?? "—"} />
      <Field label="Email" value={email} note="Sign-in is a link by email. There is no password to change." />
    </div>
  );
}

/**
 * Which business this is, and the way out of it. Its own panel since 2026-09-10, when the menu grew a Workspace row:
 * closing a workspace is about the workspace, and it sat under Settings only because nothing else did. Export is built
 * (ADR 0019), and so is closing (ADR 0022).
 *
 * Since 2026-09-10 it is also where a person's other businesses are (ADR 0006 amendment): every workspace they have,
 * a Switch beside each, and New workspace with a name. Switching and creating both reload the app, because nothing
 * cached on the page belongs to the business being arrived at.
 *
 * Since 2026-09-20 each row is the workspace itself rather than a label of it: Rename writes a new name where the old
 * one is drawn, and Delete opens the close on that row, for any of them and not only the one on screen. Renaming is
 * free and instant; closing is the two steps ADR 0022 asks for, and the Delete in the account menu leads here for them.
 */
function Workspace({ closing, onClosing }: { closing: string | null; onClosing: (id: string | null) => void }) {
  const ws = useWorkspace();
  const list = useWorkspaces();
  const go = useSwitchWorkspace();
  const make = useCreateWorkspace();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [renaming, setRenaming] = useState<string | null>(null);
  const rows = list.data?.workspaces ?? [];
  const full = list.data ? list.data.created >= list.data.limit : false;
  return (
    <div className="flex flex-col gap-5 max-w-[560px]">
      <div>
        <h3 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted mb-1.5">Your workspaces</h3>
        <div className="bg-panel border border-line rounded-[10px] divide-y divide-line">
          {rows.length ? rows.map((w) => (
            <div key={w.id}>
              {renaming === w.id ? <RenameWorkspace id={w.id} name={w.name} onDone={() => setRenaming(null)} /> : null}
              {closing === w.id ? <CloseWorkspace id={w.id} name={w.name} here={w.active} onKeep={() => onClosing(null)} /> : null}
              {renaming === w.id || closing === w.id ? null : (
                <div className="flex items-center gap-3 px-3.5 py-2.5">
                  <span className="w-7 h-7 rounded-md bg-ink text-white grid place-items-center text-[11px] font-bold shrink-0" aria-hidden>
                    {w.name.slice(0, 2).toUpperCase()}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13.5px] font-medium truncate">{w.name}</span>
                    <span className="block text-[11.5px] text-muted">{w.active ? (ws.data?.business?.industry ?? "the one you are in") : "one business, one workspace"}</span>
                  </span>
                  {w.active
                    ? <Icon name="check" size={14} className="text-coral shrink-0" />
                    : (
                      <button onClick={() => go.mutate(w.id)} disabled={go.isPending} aria-label={`Switch to ${w.name}`} className="shrink-0 px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium disabled:opacity-60">
                        Switch
                      </button>
                    )}
                  <button onClick={() => { onClosing(null); setRenaming(w.id); }} aria-label={`Rename ${w.name}`} title="Rename" className="shrink-0 p-1.5 rounded-lg text-muted hover:text-ink hover:bg-page">
                    <Icon name="pencil" size={14} />
                  </button>
                  <button onClick={() => { setRenaming(null); onClosing(w.id); }} aria-label={`Delete ${w.name}`} title="Delete" className="shrink-0 p-1.5 rounded-lg text-muted hover:text-bad hover:bg-page">
                    <Icon name="trash" size={14} />
                  </button>
                </div>
              )}
            </div>
          )) : <div className="px-3.5 py-2.5 text-[13.5px] text-muted">…</div>}
          {naming ? (
            <form
              onSubmit={(e) => { e.preventDefault(); if (name.trim()) make.mutate(name.trim()); }}
              className="flex items-center gap-2 px-3.5 py-2.5"
            >
              <input
                autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
                placeholder="The business's name" aria-label="Name of the new workspace"
                className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-line-2 bg-page text-[13px]"
              />
              <button type="submit" disabled={!name.trim() || make.isPending} className="shrink-0 px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-medium disabled:opacity-60">Create</button>
              <button type="button" onClick={() => { setNaming(false); setName(""); make.reset(); }} className="shrink-0 px-2 py-1.5 text-xs text-muted hover:text-ink">Cancel</button>
            </form>
          ) : (
            <button
              onClick={() => setNaming(true)} disabled={!list.data || full}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] text-ink-2 hover:bg-page disabled:text-muted disabled:hover:bg-transparent"
            >
              <Icon name="plus" size={14} /> New workspace
            </button>
          )}
        </div>
        {make.error || go.error ? <p className="text-[12px] text-bad mt-1.5">{String(make.error ?? go.error)}</p> : null}
        <p className="text-[12px] text-muted mt-1.5 leading-relaxed">
          A workspace is one business. What the Studio knows about it lives in the Business tab, and that page is
          sent with every brief. The name here is only the label on it, so it can be typed over at any time.{" "}
          {list.data
            ? full
              ? `${list.data.limit} workspaces is the most one person can have.`
              : `One person can have ${list.data.limit}, and all of them spend the same credit — yours.`
            : null}
        </p>
      </div>

      <div>
        <h3 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted mb-1.5">The way out</h3>
        <div className="bg-panel border border-line rounded-[10px] divide-y divide-line">
          <TakeEverything />
        </div>
      </div>
    </div>
  );
}

/** A name as a person types it. The API compares it the same way (`closing.ts`). */
const said = (name: string) => name.trim().replace(/\s+/g, " ").toLowerCase();

/**
 * Closing a workspace ([ADR 0022](../../../../docs/adr/0022-closing-a-workspace-locks-it-at-once-and-leaves-a-tombstone.md)),
 * asked on the row of the workspace it closes. Twice: the first step says what happens, and the second wants the
 * workspace's name typed. The API asks for the name too, so the second yes is not only this screen's.
 *
 * `here` is the workspace on screen, which is the only one this tab can export: its state is read from the row below
 * rather than asked for again — an observer that never fetches — so opening the panel costs the same three requests
 * it always did (docs/17 §performance 4). For any other workspace the API still refuses a close while an export of it
 * is being made, and says so; this screen has nothing to check, because it is not the one it is drawing.
 *
 * Closing the one on screen is followed by a reload, because the business on screen is one this session can no longer
 * be in. Closing another leaves the person here, with a list one row shorter.
 */
function CloseWorkspace({ id, name, here, onKeep }: { id: string; name: string; here: boolean; onKeep: () => void }) {
  const exports = useQuery({ queryKey: ["exports"], queryFn: api.exports, enabled: false });
  const close = useCloseWorkspace();
  const [step, setStep] = useState<"why" | "name">("why");
  const [typed, setTyped] = useState("");
  const exporting = here && isOpen(exports.data?.exports[0]);
  const matches = name !== "" && said(typed) === said(name);
  const keep = () => { close.reset(); onKeep(); };

  return (
    <div role="group" aria-label={`Close ${name}`} className="px-3.5 py-3 flex flex-col gap-2.5">
      <span className="block text-[13.5px] font-semibold">Close {name}?</span>
      {step === "why" ? (
        <>
          <p className="text-[12.5px] text-ink-2 leading-relaxed">
            It is locked at once, for you and for anyone else in it. Thirty days later everything in it is deleted for
            good: its projects, videos and photos, and the business page. Your credit stays yours, and your other
            workspaces go on spending it.
          </p>
          <p className={`text-[12.5px] leading-relaxed ${exporting ? "text-coral-2" : "text-ink-2"}`}>
            {exporting
              ? "An export is being made. Wait for its email before closing: its link stops working once the workspace is closed."
              : here
                ? "Take everything with you first: Export, just below, zips every video and photo and emails you the link."
                : "Take everything with you first: switch to it, then Export zips every video and photo and emails you the link."}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={() => setStep("name")} disabled={exporting} className="px-3 py-1.5 rounded-lg border border-bad/40 text-bad text-xs font-medium disabled:opacity-60">Continue</button>
            <button onClick={keep} className="px-2 py-1.5 text-xs text-muted hover:text-ink">Keep it</button>
          </div>
        </>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); if (matches && !exporting) close.mutate({ id, name: typed }); }} className="flex flex-col gap-2">
          <label className="text-[12.5px] text-ink-2">
            Type <b>{name}</b> to close it for good.
            <input
              autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} maxLength={200}
              aria-label="The name of the workspace to close"
              className="mt-1.5 block w-full px-2.5 py-1.5 rounded-lg border border-line-2 bg-page text-[13px]"
            />
          </label>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={!matches || exporting || close.isPending} className="px-3 py-1.5 rounded-lg bg-bad text-white text-xs font-medium disabled:opacity-60">
              {close.isPending ? "Closing…" : "Close for good"}
            </button>
            <button type="button" onClick={keep} className="px-2 py-1.5 text-xs text-muted hover:text-ink">Keep it</button>
          </div>
        </form>
      )}
      {close.error ? <p role="alert" className="text-[12px] text-bad">{String(close.error)}</p> : null}
    </div>
  );
}

const isOpen = (e: ExportView | undefined) => e?.status === "queued" || e?.status === "running";

const bytesSaid = (bytes: number) =>
  bytes >= 1e9 ? `${(bytes / 1e9).toFixed(1)} GB` : bytes >= 1e6 ? `${Math.round(bytes / 1e6)} MB` : `${Math.max(1, Math.round(bytes / 1e3))} KB`;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Take everything with you (ADR 0019). A job rather than a download: the button asks for one, the worker zips it, and
 * an email says when it is ready — so this row only ever says where the export has got to, and nobody has to keep it
 * open. Each Download goes through the API, which checks who is asking before it hands over a link that lasts five
 * minutes; a big workspace comes in parts of up to 2 GB, each its own link.
 */
function TakeEverything() {
  const qc = useQueryClient();
  const exports = useQuery({
    queryKey: ["exports"],
    queryFn: api.exports,
    // Asked again only while one is being made. A finished export does not change until it ends.
    refetchInterval: (q) => (isOpen(q.state.data?.exports[0]) ? 5000 : false),
  });
  const start = useMutation({ mutationFn: api.requestExport, onSettled: () => qc.invalidateQueries({ queryKey: ["exports"] }) });
  const latest = exports.data?.exports[0];
  const busy = isOpen(latest) || start.isPending;
  const ready = latest?.status === "ready" ? latest : null;

  const saying = busy
    ? "Being zipped now. We will email you when it is ready — there is no need to keep this open."
    : ready
      ? `${plural(ready.videos, "video")} and ${plural(ready.photos, "photo")}, ${bytesSaid(ready.bytes)}. Ready until ${day(ready.expiresAt!)}.`
      : latest?.status === "failed"
        ? "The last export did not finish, and nothing from it was kept. Try again."
        : "The latest version of every video, every photo you uploaded, and the business profile, as a zip.";

  return (
    <div className="px-3.5 py-2.5">
      <div className="flex items-center gap-3">
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px]">Take everything with you</span>
          <span className="block text-[11.5px] text-muted leading-snug">{saying}</span>
        </span>
        <button
          onClick={() => start.mutate()}
          disabled={busy || !exports.data}
          className="shrink-0 px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium disabled:opacity-60"
        >
          {busy ? "Preparing…" : ready ? "Export again" : "Export"}
        </button>
      </div>
      {ready ? (
        <ul className="mt-2 flex flex-col gap-1">
          {ready.parts.map((p) => (
            <li key={p.n} className="flex items-baseline gap-2 text-[12.5px]">
              <a href={p.url} className="font-medium text-coral hover:text-coral-2">
                Download{ready.parts.length > 1 ? ` part ${p.n} of ${ready.parts.length}` : ""}
              </a>
              <span className="text-muted tabular-nums">{bytesSaid(p.bytes)}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {start.error ? (
        <p role="alert" className="mt-1.5 text-[12px] text-bad">
          {start.error instanceof ApiError && start.error.status === 429
            ? "Three exports a day is the most one workspace can ask for. Try again tomorrow."
            : String(start.error)}
        </p>
      ) : null}
    </div>
  );
}

/** Billing, whole. There is no plan to manage, no seat to add and nothing to cancel — only credit. */
function Credits() {
  const ws = useWorkspace();
  const credits = useQuery({ queryKey: ["credits"], queryFn: api.credits });
  const expiring = Number(credits.data?.expiringUsd ?? 0);
  // Nobody arrives with credit (ADR 0048), so the first time this screen is opened it is a place to start rather
  // than a statement of what is left.
  const firstTime = credits.data ? credits.data.lots.total === 0 : false;
  const connected = credits.data?.cardPayments ?? false;
  const packs = credits.data?.packs ?? [...TOP_UPS];

  return (
    <div className="flex flex-col gap-5">
      {/*
        The card. What there is, what is running out and the three ways to add to it are one object, because they
        are one subject: a person who has just read the number is standing exactly where they would top it up.
        The words about what credit is come after, as the fine print they are, so the number is not fighting a
        paragraph for the eye (it used to sit beside one).
      */}
      <section className="bg-panel border border-line rounded-[10px] overflow-hidden">
        <div className="px-4 pt-4 pb-3.5 flex items-end justify-between gap-x-6 gap-y-3 flex-wrap">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">{firstTime ? "Your balance" : "Credit left"}</div>
            <div className="font-display text-[44px] font-bold tabular-nums leading-none tracking-[-.02em] mt-2">${ws.data?.balanceUsd ?? "—"}</div>
          </div>
          <p className="text-[12px] leading-snug max-w-[30ch] sm:text-right">
            {expiring > 0 ? (
              <span className="text-coral-2"><b>${expiring.toFixed(2)} expires within two months.</b> The oldest is always spent first.</span>
            ) : firstTime ? (
              <span className="text-muted">Add some to start. It is spent as you go, and nothing is taken monthly.</span>
            ) : (
              <span className="text-muted">Lasts a year from the day it is bought, and the oldest is always spent first.</span>
            )}
          </p>
        </div>
        <TopUp packs={packs} connected={connected} firstTime={firstTime} billingPage={credits.data?.billingPage ?? false} />
      </section>

      <p className="text-[12.5px] text-muted leading-relaxed">
        {firstTime
          ? <>Joogoos is paid for as it is used, and nothing runs until there is credit here. Credits are dollars: every call is charged as it runs and listed under Spending. There is no subscription and nothing is taken monthly.</>
          : <>Credits are dollars, and they are yours: every workspace you have spends from this one balance. Every call is charged as it runs and listed under Spending. There is no subscription.</>}
        {" "}
        {connected
          ? <>Paid on Stripe's own page: no card details are typed into Joogoos, and none is kept by it. ${packs[0] ?? 10} is the smallest top-up — below that the card fee takes a tenth of it.</>
          : <>${packs[0] ?? 10} is the smallest top-up: below that the card fee takes a tenth of it.</>}
      </p>
    </div>
  );
}

/**
 * Where the credit went, and the ceilings on it: the limits, the lots, the ledger and every call. Its own panel since
 * 2026-09-24 — Credits is the card and nothing else, so somebody who came to add credit is not reading a statement
 * to find the button (the owner asked for it the day an empty account started being held on Credits).
 */
function Spending() {
  const ws = useWorkspace();
  const credits = useQuery({ queryKey: ["credits"], queryFn: api.credits });
  const limits = credits.data?.limits;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h3 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted mb-1.5">Your limits</h3>
        <div className="bg-panel border border-line rounded-[10px] divide-y divide-line">
          <Cap
            label="A day"
            which="daily"
            capUsd={limits?.dailyCapUsd ?? ws.data?.dailyCapUsd ?? null}
            spentUsd={limits?.dayUsd}
            note="Nothing runs past this in one day, across all your workspaces. A step it refuses is not run and costs nothing, and the post stops there."
          />
          <Cap
            label="A month"
            which="monthly"
            capUsd={limits?.monthlyCapUsd ?? null}
            spentUsd={limits?.monthUsd}
            note="Your own ceiling on a calendar month. With none set, only the daily limit and your credit stop anything; reaching it is one tap to raise, and nothing you have made is ever lost."
          />
        </div>
      </div>

      <Lots first={credits.data?.lots} />
      <Ledger first={credits.data?.ledger} />
      <Activity />
    </div>
  );
}

/**
 * Buying credit ([ADR 0012](../../../../docs/adr/0012-pay-as-you-go-with-a-monthly-cap.md)): the foot of the card.
 *
 * The packs themselves are `TopUpPacks` in `ui/money.tsx`, because the Studio offers the same three at the moment
 * somebody runs out — this is the heading and the one line about where the card number goes. The sentence about the
 * smallest pack and the fee is fine print, and sits under the card with the rest of it.
 */
function TopUp({ packs, connected, firstTime, billingPage }: { packs: number[]; connected: boolean; firstTime: boolean; billingPage: boolean }) {
  // Their invoices and receipts, and the billing details printed on them, on Stripe's own page (34). Opened on the
  // press rather than linked, because the link is made for this person and lasts minutes.
  const open = useMutation({ mutationFn: api.billingPage, onSuccess: (r) => page.go(r.url) });
  return (
    <div className="border-t border-line bg-page/70 px-4 pt-3 pb-3.5">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">{firstTime ? "Start with" : "Top up"}</h3>
        {billingPage ? (
          <button onClick={() => open.mutate()} disabled={open.isPending} className="text-[11.5px] font-medium text-muted hover:text-ink underline underline-offset-2 disabled:opacity-50">
            {open.isPending ? "Opening…" : "Invoices & billing details"}
          </button>
        ) : connected ? <span className="text-[11.5px] text-muted">Paid on Stripe's own page</span> : null}
      </div>
      {open.error ? <p role="alert" className="text-xs text-bad mb-2">{String((open.error as Error).message)}</p> : null}
      <TopUpPacks packs={packs} connected={connected} />
    </div>
  );
}

/**
 * One ceiling the person sets themselves, and where they stand against it (guards 3 and 4, docs/13).
 *
 * Both of them are here rather than only the monthly one, because a number a customer can read and not change is a
 * number they do not believe — and the daily limit has been readable and unchangeable since the day it was drawn.
 *
 * Raising is deliberately one field and one press ([ADR 0012](../../../../docs/adr/0012-pay-as-you-go-with-a-monthly-cap.md)
 * point 3): a ceiling that is awkward to raise is a ceiling people set too high out of fear, which defeats it.
 */
function Cap({ label, which, capUsd, spentUsd, note }: {
  label: string;
  which: "daily" | "monthly";
  capUsd: string | null;
  spentUsd: string | undefined;
  note: string;
}) {
  const moved = useMoneyMoved();
  const [editing, setEditing] = useState(false);
  /**
   * Taking a ceiling off, which only the monthly one can be: there is always a daily limit, so there is nothing to
   * clear on that row. Read off `which` rather than from a `removable` prop, which was a trap — passed to the daily
   * row it would have offered a Remove that quietly cleared the *monthly* cap.
   */
  const removable = which === "monthly";
  const remove = useMutation({
    mutationFn: () => api.setLimits({ monthlyUsd: null }),
    onSuccess: () => { setEditing(false); moved(); },
  });
  const spent = Number(spentUsd ?? 0);
  const left = capUsd === null ? null : Math.max(0, Number(capUsd) - spent);

  return (
    <div className="px-3.5 py-3 flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold tabular-nums">
          {label}: {capUsd === null ? <span className="font-normal text-muted">no limit set</span> : `$${Number(capUsd).toFixed(2)}`}
          {spentUsd !== undefined ? (
            <span className="font-normal text-muted"> · ${spent.toFixed(2)} spent{left !== null ? `, $${left.toFixed(2)} left` : ""}</span>
          ) : null}
        </div>
        {capUsd !== null && spentUsd !== undefined ? (
          <Bar spent={spent} cap={Number(capUsd)} />
        ) : null}
        <div className="text-[12px] text-muted leading-relaxed">{note}</div>
        {editing ? (
          <div className="mt-2">
            <RaiseLimit which={which} currentUsd={capUsd} onDone={() => setEditing(false)} />
          </div>
        ) : null}
        {remove.error ? <p role="alert" className="text-[12px] text-bad mt-1">{String(remove.error)}</p> : null}
      </div>
      {!editing ? (
        <span className="shrink-0 flex items-center gap-1.5">
          <button
            onClick={() => setEditing(true)}
            className="px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium hover:bg-page"
          >
            {capUsd === null ? "Set one" : "Change"}
          </button>
          {removable && capUsd !== null ? (
            <button onClick={() => remove.mutate()} disabled={remove.isPending} className="px-2 py-1.5 text-xs text-muted hover:text-ink disabled:opacity-60">
              Remove
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

/**
 * How much of a ceiling has gone, drawn rather than subtracted. Ink until the last tenth, then coral: the colour
 * says "about to stop you" and nothing else on this screen says it.
 */
function Bar({ spent, cap }: { spent: number; cap: number }) {
  const share = cap > 0 ? Math.min(1, spent / cap) : 0;
  return (
    <span className="block h-1.5 rounded-full bg-line overflow-hidden max-w-[320px] my-1.5" aria-hidden>
      <span className={`block h-full rounded-full ${share >= 0.9 ? "bg-coral" : "bg-ink"}`} style={{ width: `${Math.round(share * 100)}%` }} />
    </span>
  );
}

/**
 * A page of purchases or of movements. The first came with `/credits`, so opening the panel costs nothing more; a
 * later one is asked for when it is turned to. Under the `credits` key, so money moving asks for it again too.
 */
function usePaged<T>(which: "lots" | "ledger", first: Page<T> | undefined, read: (page: number) => Promise<Page<T>>) {
  const [page, setPage] = useState(0);
  const later = useQuery({ queryKey: ["credits", which, page], queryFn: () => read(page), enabled: page > 0, placeholderData: keepPreviousData });
  const shown = (page > 0 ? later.data : undefined) ?? first;
  return { rows: shown?.rows ?? [], total: shown?.total ?? 0, page, setPage };
}

function Lots({ first }: { first: Page<CreditLot> | undefined }) {
  const pages = usePaged("lots", first, api.lots);
  if (!pages.total) return null;
  return (
    <div>
      <h3 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted mb-1.5">What you have bought</h3>
      <table className="w-full text-[12.5px] border-separate border-spacing-0">
        <thead>
          <tr className="text-[10.5px] font-bold uppercase tracking-[.06em] text-muted text-left">
            {["Bought", "Paid", "Left", "Expires", ""].map((h, i) => (
              <th key={h || i} className={`border-b border-line pb-1.5 ${i > 0 && i < 4 ? "text-right" : ""}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pages.rows.map((l) => (
            <tr key={l.id}>
              <td className="border-b border-line py-2">{day(l.purchasedAt)}</td>
              <td className="border-b border-line py-2 text-right tabular-nums">${Number(l.purchasedUsd).toFixed(2)}</td>
              <td className="border-b border-line py-2 text-right tabular-nums">${Number(l.remainingUsd).toFixed(2)}</td>
              <td className="border-b border-line py-2 text-right tabular-nums">{day(l.expiresAt)}</td>
              <td className="border-b border-line py-2 pl-3">
                <Tag tone={l.status === "active" ? "good" : "flat"}>{l.status}</Tag>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager page={pages.page} total={pages.total} onPage={pages.setPage} label="Pages of what you have bought" />
    </div>
  );
}

function Ledger({ first }: { first: Page<LedgerEntry> | undefined }) {
  const pages = usePaged("ledger", first, api.ledger);
  if (!pages.total) return null;
  return (
    <div>
      <h3 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted mb-1.5">Every movement</h3>
      <ul className="bg-panel border border-line rounded-[10px] divide-y divide-line">
        {pages.rows.map((e) => (
          <li key={e.id} className="flex items-baseline gap-3 px-3.5 py-2">
            <span className="text-[12.5px] flex-1 min-w-0 truncate">
              {e.note ?? e.kind}
              {/* Where it was spent: the balance is one, so the list is the only place the businesses come apart. */}
              {e.workspaceName ? <span className="text-muted"> · {e.workspaceName}</span> : null}
            </span>
            <span className="text-[11.5px] text-muted shrink-0">{when(e.createdAt)}</span>
            <span className={`text-[12.5px] tabular-nums shrink-0 w-20 text-right ${Number(e.deltaUsd) > 0 ? "text-good" : ""}`}>
              {Number(e.deltaUsd) > 0 ? "+" : ""}${Math.abs(Number(e.deltaUsd)).toFixed(4)}
            </span>
          </li>
        ))}
      </ul>
      <Pager page={pages.page} total={pages.total} onPage={pages.setPage} label="Pages of every movement" />
    </div>
  );
}

const EMAILS: Array<[string, string]> = [
  ["A video is ready", "the render takes a few minutes and you should not have to sit and watch it"],
  ["Credit is running low", "under two dollars, once, rather than every day"],
  ["A publish failed", "the one email you cannot afford to miss"],
];

/**
 * What is left once billing and the workspace have their own panels: what we are allowed to email about.
 * Deliberately short. Everything the Studio knows about the business is a document in its own tab, not a settings
 * form, and the daily limit lives with the money it governs. Where posts go left here on 2026-09-20 for
 * Business → Channels (docs/25, ADR 0038): which Instagram this company posts as is a fact about the business, not
 * a preference, so one line says where it went rather than a second list of the same six accounts.
 *
 * The first switch is real (ADR 0024, amended 2026-10-05): your reports' news by email, off until you turn it on,
 * because the bell beside the credit already says it. The rest are not stored yet, and each says so rather than moving
 * and forgetting: a switch that goes back to where it was on the next page load is worse than a switch that admits it
 * is a drawing.
 */
function Settings() {
  return (
    <div className="flex flex-col gap-5 max-w-[560px]">
      <p className="text-[12.5px] text-muted leading-relaxed">
        The accounts your posts go out on are connected under <b>Business → Channels</b>, with the rest of where
        your business is.
      </p>

      <div>
        <h3 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted mb-1.5">What we email you about</h3>
        <div className="bg-panel border border-line rounded-[10px] divide-y divide-line">
          <ReportEmails />
          {EMAILS.map(([label, note]) => (
            <div key={label} className="flex items-center gap-3 px-3.5 py-2.5">
              <span className="min-w-0 flex-1">
                <span className="block text-[13.5px]">{label}</span>
                <span className="block text-[11.5px] text-muted leading-snug">{note}</span>
              </span>
              <Switch />
            </div>
          ))}
        </div>
        <p className="text-[12px] text-muted mt-1.5">The three under the first are not sent yet, and their switches do not change anything.</p>
      </div>
    </div>
  );
}

/** Your reports' news by email as well as on the bell. Yours, in every workspace, because it is your inbox. */
function ReportEmails() {
  const qc = useQueryClient();
  const on = useReportNews().data?.emails ?? false;
  const turn = useMutation({
    mutationFn: (next: boolean) => api.setReportEmails(next),
    onSuccess: () => qc.invalidateQueries({ queryKey: REPORT_NEWS_KEY }),
  });
  const shown = turn.isPending ? turn.variables : on;
  return (
    <div className="flex items-center gap-3 px-3.5 py-2.5">
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px]">Your reports</span>
        <span className="block text-[11.5px] text-muted leading-snug">
          {turn.error
            ? <span role="alert" className="text-bad">{turn.error.message}</span>
            : "when we answer one, or a release fixes it. The bell beside your credit says it either way"}
        </span>
      </span>
      <button
        role="switch" aria-checked={shown} aria-label="Email me about my reports"
        onClick={() => turn.mutate(!on)} disabled={turn.isPending}
        className={`shrink-0 w-9 h-5 rounded-full relative transition-colors disabled:opacity-60 ${shown ? "bg-ink" : "bg-line"}`}
      >
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-[left] ${shown ? "left-[18px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}

/** A switch that is drawn off and does nothing, and does not pretend otherwise: none of these emails exists yet. */
function Switch() {
  return (
    <span className="shrink-0 w-9 h-5 rounded-full bg-line relative opacity-60" title="Not sent yet" aria-hidden>
      <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white" />
    </span>
  );
}

/**
 * Every call, and what it cost — the part of Credits that makes "we log everything" mean something to the person
 * paying for it.
 */
function Activity() {
  const [only, setOnly] = useState<CallFilter>("all");
  const [page, setPage] = useState(0);
  // Filtered and paged on the server, so "failed" finds every failure there has been and not those on this page.
  const spend = useQuery({ queryKey: ["spend", only, page], queryFn: () => api.spend(page, only), placeholderData: keepPreviousData });
  const alerts = useQuery({ queryKey: ["alerts"], queryFn: api.alerts });
  const calls = spend.data?.calls.rows ?? [];

  return (
    <div className="flex flex-col gap-3.5">
      <h3 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Every call it has paid for</h3>
      <div className="flex items-center gap-1.5 flex-wrap -mt-1.5">
        {(["all", "running", "failed"] as const).map((f) => (
          <button
            key={f}
            onClick={() => { setOnly(f); setPage(0); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize ${only === f ? "bg-ink text-white" : "bg-page text-ink-2 hover:bg-line"}`}
          >
            {f === "all" ? "Everything" : f}
          </button>
        ))}
        <span className="ml-auto text-[12.5px] text-muted tabular-nums">${spend.data?.totalUsd ?? "—"} charged in all</span>
      </div>

      {alerts.data?.length ? <Refusals alerts={alerts.data} /> : null}

      {calls.length ? (
        <div>
          <table className="w-full text-[12.5px] border-separate border-spacing-0">
            <thead>
              <tr className="text-[10.5px] font-bold uppercase tracking-[.06em] text-muted text-left">
                {["When", "What", "Where it went", "", "Read / wrote", "Took", "Cost"].map((h, i) => (
                  <th key={h || i} className={`border-b border-line pb-1.5 ${i > 3 ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {calls.map((c) => <Call key={c.id} c={c} />)}
            </tbody>
          </table>
          <Pager page={page} total={spend.data?.calls.total ?? 0} onPage={setPage} label="Pages of every call" />
        </div>
      ) : (
        <p className="text-[13px] text-muted py-6">
          {spend.data ? "Nothing has been run yet. A call appears here as soon as one runs: the Business lookup, a photo upload or Generate." : "…"}
        </p>
      )}

      <p className="text-[12px] text-muted leading-relaxed">
        Every call that ran is here, including the ones that failed. A call a limit refused never ran, so it is not
        a row: the latest refusals are shown above the list. The reference beside a failure is the one to read down
        the phone.
      </p>
    </div>
  );
}

/** What the provider counted: tokens where it counts tokens, otherwise whatever it did count. */
function measured(c: CallView): string {
  if (c.inputTokens !== null || c.outputTokens !== null) {
    return `${count(c.inputTokens)} / ${count(c.outputTokens)}`;
  }
  const other = (c.units ?? []).filter((u) => !u.kind.startsWith("tokens"));
  if (!other.length) return "—";
  return other.map((u) => `${count(u.count)} ${u.kind.replace(/_/g, " ")}${u.count === 1 ? "" : "s"}`).join(", ");
}

const count = (n: number | null) => (n === null ? "—" : n >= 1000 ? `${Math.round(n / 100) / 10}k` : String(n));

function Call({ c }: { c: CallView }) {
  const money = c.chargedUsd ?? c.reservedUsd;
  return (
    <tr>
      <td className="border-b border-line py-2 text-muted whitespace-nowrap">{when(c.createdAt)}</td>
      <td className="border-b border-line py-2 font-medium">
        {c.operation.replace(/_/g, " ")}
        {c.workspaceName ? <span className="block text-[11px] font-normal text-muted">{c.workspaceName}</span> : null}
      </td>
      <td className="border-b border-line py-2 text-muted truncate max-w-[180px]">{c.provider} · {c.model}</td>
      <td className="border-b border-line py-2">
        <Tag tone={c.status === "failed" ? "bad" : c.status === "succeeded" ? "good" : "flat"}>{c.status}</Tag>
        {c.error ? <span className="block text-[11px] text-bad mt-0.5">{c.error}</span> : null}
      </td>
      {/* What the price was actually made of. Tokens for the models, requests for the rest — the units a bill
          would itemise, which is the only way a total means anything. */}
      <td className="border-b border-line py-2 text-right tabular-nums text-muted whitespace-nowrap">{measured(c)}</td>
      <td className="border-b border-line py-2 text-right tabular-nums text-muted">{c.durationMs ? `${(c.durationMs / 1000).toFixed(1)}s` : "—"}</td>
      <td className="border-b border-line py-2 text-right tabular-nums">
        {money ? `$${Number(money).toFixed(4)}` : "—"}
        {c.chargedUsd === null && c.reservedUsd ? <span className="block text-[10.5px] text-muted">held</span> : null}
      </td>
    </tr>
  );
}

/** What a guard refused, in the customer's words rather than the owner's (docs/13). */
function Refusals({ alerts }: { alerts: AlertView[] }) {
  const said: Record<string, string> = {
    global_cap: "everything was paused", workspace_cap: "your daily limit was reached",
    project_budget: "the project reached its budget", attempts_exhausted: "it was tried three times and stopped",
    deadline_expired: "it took too long and was stopped", paused_refusal: "everything was paused",
    insufficient_credits: "there was not enough credit", workspace_closed: "the workspace had been closed",
  };
  return (
    <ul className="bg-panel border border-line rounded-[10px] divide-y divide-line">
      {alerts.slice(0, 4).map((a) => (
        <li key={a.id} className="flex items-baseline gap-3 px-3.5 py-2 text-[12.5px]">
          <Icon name="wand" size={13} className="text-muted shrink-0 translate-y-0.5" />
          <span className="flex-1">{said[a.kind] ?? a.kind}</span>
          <span className="text-[11.5px] text-muted shrink-0">{when(a.createdAt)}</span>
        </li>
      ))}
    </ul>
  );
}

function Field({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div>
      <h3 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted mb-1.5">{label}</h3>
      <div className="bg-panel border border-line rounded-[10px] px-3.5 py-2.5 text-[13.5px]">{value}</div>
      {note ? <p className="text-[12px] text-muted mt-1.5">{note}</p> : null}
    </div>
  );
}

function Tag({ tone, children }: { tone: "good" | "bad" | "flat"; children: React.ReactNode }) {
  const cls = tone === "good" ? "bg-good/12 text-good" : tone === "bad" ? "bg-bad/10 text-bad" : "bg-line text-muted";
  return <span className={`px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wide ${cls}`}>{children}</span>;
}

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

function when(iso: string) {
  const mins = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return day(iso);
}
